// src/components/Layout/FiltersSidebar.js
import React, { useEffect, useState } from 'react';
import { useFilters } from '../../context/FiltersContext';
import './FiltersSidebar.css';
import MultiSelect from '../MultiSelect';
import YearRangeSlider from '../YearRangeSlider';
import ScoreThresholdSlider from '../ScoreThresholdSlider';
import MonthRangeSlider from '../MonthRangeSlider';

const PATENT_SCORE_FILTERS = [
  { key: 'novelty', label: 'Novelty' },
  { key: 'disruption', label: 'Disruption' },
  { key: 'industrialization', label: 'Industrialization' },
];

function FiltersSidebar({
  currentPage = 'database',
  options = {},
  activeFilters = {},
  onFilterChange,
  onResetAllFilters,
  onSaveCurrentFilters,
  savingCurrentFilter = false,
}) {
  const { isCollapsed, toggleCollapse } = useFilters();
  const [openAccordions, setOpenAccordions] = useState({});

  // Define filters configuration for each page
  const isDatabasePage = currentPage === 'database';
  const isConferencesPage = currentPage === 'conferences';
  const isPatentsPage = currentPage === 'patents';

  // Initialize Lucide icons when component mounts or isCollapsed changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.lucide && typeof window.lucide.createIcons === 'function') {
      setTimeout(() => {
        window.lucide.createIcons();
      }, 100);
    }
  }, [isCollapsed]);

  const toggleAccordion = (filterName) => {
    setOpenAccordions(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const statusOptions = [
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
    { value: 'all', label: 'All' }
  ];

  const handleStatusChange = (value) => {
    if (onFilterChange) {
      onFilterChange('status', value === 'all' ? '' : value);
    }
  };

  const handleOptionToggle = (filterKey, option) => {
    if (!onFilterChange) return;
    const currentValues = activeFilters[filterKey] || [];
    let newValues;
    if (currentValues.includes(option)) {
      newValues = currentValues.filter(v => v !== option);
    } else {
      newValues = [...currentValues, option];
    }
    onFilterChange(filterKey, newValues);
  };

  return (
    <>
      {isCollapsed ? (
        <div className="filters-sidebar-collapsed">
          <button
            type="button"
            className="filters-sidebar-reopen-btn"
            onClick={toggleCollapse}
            title="Open filters panel"
          >
            <i data-lucide="sliders-horizontal"></i>
            <span className="filters-sidebar-reopen-text">Filters</span>
          </button>
        </div>
      ) : (
        <aside className="filters-sidebar">
          <div className="filters-sidebar-header">
            <h2 className="filters-sidebar-title">Filters</h2>
            <div className="filters-sidebar-actions">
              <button
                type="button"
                className="filters-sidebar-action-btn"
                onClick={onSaveCurrentFilters}
                disabled={savingCurrentFilter}
                title="Save current filters"
              >
                <i data-lucide="save"></i>
              </button>
              <button
                type="button"
                className="filters-sidebar-action-btn"
                onClick={onResetAllFilters}
                title="Reset all filters"
              >
                <i data-lucide="rotate-ccw"></i>
              </button>
              <button
                type="button"
                className="filters-sidebar-action-btn"
                onClick={toggleCollapse}
                title="Collapse filters panel"
              >
                <i data-lucide="chevron-left"></i>
              </button>
            </div>
          </div>

          <div className="filters-sidebar-body">
            {/* DATABASE FILTERS */}
            {isDatabasePage && (
              <>
            {/* Organization */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('OEM')}
              >
                <span className="filters-accordion-title">Organization</span>
                <i data-lucide={openAccordions.OEM ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.OEM && (
                <div className="filters-accordion-content">
                  {(options.OEM || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.OEM || []).includes(option)}
                        onChange={() => handleOptionToggle('OEM', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.OEM || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Brand */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('brand')}
              >
                <span className="filters-accordion-title">Brand</span>
                <i data-lucide={openAccordions.brand ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.brand && (
                <div className="filters-accordion-content">
                  {(options.brand || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.brand || []).includes(option)}
                        onChange={() => handleOptionToggle('brand', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.brand || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Car Model */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('car_model')}
              >
                <span className="filters-accordion-title">Car Model</span>
                <i data-lucide={openAccordions.car_model ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.car_model && (
                <div className="filters-accordion-content">
                  {(options.car_model || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.car_model || []).includes(option)}
                        onChange={() => handleOptionToggle('car_model', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.car_model || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* System */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('system')}
              >
                <span className="filters-accordion-title">System</span>
                <i data-lucide={openAccordions.system ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.system && (
                <div className="filters-accordion-content">
                  {(options.system || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.system || []).includes(option)}
                        onChange={() => handleOptionToggle('system', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.system || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Subsystem */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('subsystem')}
              >
                <span className="filters-accordion-title">Subsystem</span>
                <i data-lucide={openAccordions.subsystem ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.subsystem && (
                <div className="filters-accordion-content">
                  {(options.subsystem || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.subsystem || []).includes(option)}
                        onChange={() => handleOptionToggle('subsystem', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.subsystem || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Part */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('part')}
              >
                <span className="filters-accordion-title">Part</span>
                <i data-lucide={openAccordions.part ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.part && (
                <div className="filters-accordion-content">
                  {(options.part || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.part || []).includes(option)}
                        onChange={() => handleOptionToggle('part', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.part || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* Material */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('material')}
              >
                <span className="filters-accordion-title">Material</span>
                <i data-lucide={openAccordions.material ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.material && (
                <div className="filters-accordion-content">
                  {(options.material || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.material || []).includes(option)}
                        onChange={() => handleOptionToggle('material', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.material || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Technology */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('technology')}
              >
                <span className="filters-accordion-title">Technology</span>
                <i data-lucide={openAccordions.technology ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.technology && (
                <div className="filters-accordion-content">
                  {(options.technology || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.technology || []).includes(option)}
                        onChange={() => handleOptionToggle('technology', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.technology || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* Type */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('type')}
              >
                <span className="filters-accordion-title">Type</span>
                <i data-lucide={openAccordions.type ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.type && (
                <div className="filters-accordion-content">
                  {(options.type || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.type || []).includes(option)}
                        onChange={() => handleOptionToggle('type', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.type || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Year */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('year')}
              >
                <span className="filters-accordion-title">Year</span>
                <i data-lucide={openAccordions.year ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.year && (
                <div className="filters-accordion-content">
                  <YearRangeSlider
                    availableYears={options.year_of_publication || []}
                    selectedRange={activeFilters.year_of_publication || []}
                    onChange={(range) => onFilterChange && onFilterChange('year_of_publication', range)}
                  />
                </div>
              )}
            </div>

            {/* Status */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('status')}
              >
                <span className="filters-accordion-title">Status</span>
                <i data-lucide={openAccordions.status ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.status && (
                <div className="filters-accordion-content">
                  <select
                    className="filters-sidebar-filter-select"
                    value={activeFilters.status || 'all'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
              </>
            )}

            {/* CONFERENCES FILTERS */}
            {isConferencesPage && (
              <>
            {/* Country */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('Country')}
              >
                <span className="filters-accordion-title">Country</span>
                <i data-lucide={openAccordions.Country ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.Country && (
                <div className="filters-accordion-content">
                  {(options.Country || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.Country || []).includes(option)}
                        onChange={() => handleOptionToggle('Country', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.Country || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Price */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('Price')}
              >
                <span className="filters-accordion-title">Price</span>
                <i data-lucide={openAccordions.Price ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.Price && (
                <div className="filters-accordion-content">
                  {(options.Price || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.Price || []).includes(option)}
                        onChange={() => handleOptionToggle('Price', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.Price || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* Type */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('type')}
              >
                <span className="filters-accordion-title">Type</span>
                <i data-lucide={openAccordions.type ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.type && (
                <div className="filters-accordion-content">
                  {(options.type || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.type || []).includes(option)}
                        onChange={() => handleOptionToggle('type', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.type || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Year */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('year')}
              >
                <span className="filters-accordion-title">Year</span>
                <i data-lucide={openAccordions.year ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.year && (
                <div className="filters-accordion-content">
                  {(options.year || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.year || []).includes(option)}
                        onChange={() => handleOptionToggle('year', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.year || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Topic */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('topic')}
              >
                <span className="filters-accordion-title">Topic</span>
                <i data-lucide={openAccordions.topic ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.topic && (
                <div className="filters-accordion-content">
                  {(options.topic || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.topic || []).includes(option)}
                        onChange={() => handleOptionToggle('topic', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.topic || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>
              </>
            )}

            {/* PATENTS FILTERS */}
            {isPatentsPage && (
              <>
            {/* Applicants */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('applicants')}
              >
                <span className="filters-accordion-title">Applicant</span>
                <i data-lucide={openAccordions.applicants ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.applicants && (
                <div className="filters-accordion-content">
                  {(options.applicants || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.applicants || []).includes(option)}
                        onChange={() => handleOptionToggle('applicants', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.applicants || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* Category */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('category')}
              >
                <span className="filters-accordion-title">Category</span>
                <i data-lucide={openAccordions.category ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.category && (
                <div className="filters-accordion-content">
                  {(options.category || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.category || []).includes(option)}
                        onChange={() => handleOptionToggle('category', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.category || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Subsystem */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('subsystem')}
              >
                <span className="filters-accordion-title">Subsystem</span>
                <i data-lucide={openAccordions.subsystem ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.subsystem && (
                <div className="filters-accordion-content">
                  {(options.subsystem || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.subsystem || []).includes(option)}
                        onChange={() => handleOptionToggle('subsystem', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.subsystem || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            {/* Part */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('part')}
              >
                <span className="filters-accordion-title">Part</span>
                <i data-lucide={openAccordions.part ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.part && (
                <div className="filters-accordion-content">
                  {(options.part || []).map(option => (
                    <label key={option} className="filters-accordion-option">
                      <input
                        type="checkbox"
                        checked={(activeFilters.part || []).includes(option)}
                        onChange={() => handleOptionToggle('part', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {(options.part || []).length === 0 && (
                    <div className="filters-accordion-empty">No options available</div>
                  )}
                </div>
              )}
            </div>

            <div className="filters-sidebar-filter-divider"></div>

            {/* Novelty / Disruption / Industrialization score floors */}
            {PATENT_SCORE_FILTERS.map(({ key, label }) => (
              <div className="filters-accordion" key={key}>
                <button
                  className="filters-accordion-header"
                  onClick={() => toggleAccordion(key)}
                >
                  <span className="filters-accordion-title">{label}</span>
                  <i data-lucide={openAccordions[key] ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
                </button>
                {openAccordions[key] && (
                  <div className="filters-accordion-content">
                    <ScoreThresholdSlider
                      label={label}
                      value={activeFilters[key]}
                      onChange={(value) => onFilterChange && onFilterChange(key, value)}
                    />
                  </div>
                )}
              </div>
            ))}

            <div className="filters-sidebar-filter-divider"></div>

            {/* Year */}
            <div className="filters-accordion">
              <button
                className="filters-accordion-header"
                onClick={() => toggleAccordion('publication_date')}
              >
                <span className="filters-accordion-title">Year</span>
                <i data-lucide={openAccordions.publication_date ? 'chevron-up' : 'chevron-down'} className="filters-accordion-icon"></i>
              </button>
              {openAccordions.publication_date && (
                <div className="filters-accordion-content">
                  <MonthRangeSlider
                    minMonth={options.publication_month_bounds?.min}
                    maxMonth={options.publication_month_bounds?.max}
                    selectedRange={activeFilters.publication_date || []}
                    onChange={(range) => onFilterChange && onFilterChange('publication_date', range)}
                  />
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </aside>
      )}
    </>
  );
}

export default FiltersSidebar;
